import { append, h } from './dom';

export type ModalValues = Record<string, string>;

export interface ModalOptions {
  title: string;
  body: Array<HTMLElement | string>;
  confirm?: string;
  /** Pass null for a dialog with only the confirm button. */
  cancel?: string | null;
  onMount?(form: HTMLFormElement, close: (values: ModalValues | null) => void): void;
}

/** A dialog with a form body. Resolves with the form's values, or null when dismissed. */
export function modal(o: ModalOptions): Promise<ModalValues | null> {
  return new Promise((resolve) => {
    const form = h('form', { class: 'modal__form' });
    append(form, o.body);
    const acts = h('div', { class: 'modal__acts' });
    const cancelLabel = o.cancel === undefined ? 'Cancel' : o.cancel;
    const cancel = cancelLabel === null ? null : h('button', { type: 'button', class: 'btn' }, cancelLabel);
    if (cancel) acts.appendChild(cancel);
    acts.appendChild(h('button', { type: 'submit', class: 'btn btn--primary' }, o.confirm ?? 'Done'));
    form.appendChild(acts);
    const host = h(
      'div',
      { class: 'modal', attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': o.title } },
      h('div', { class: 'modal__card' }, h('h2', { class: 'modal__title' }, o.title), form),
    );
    const close = (values: ModalValues | null) => {
      host.remove();
      document.removeEventListener('keydown', onKey);
      resolve(values);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(null);
    };
    document.addEventListener('keydown', onKey);
    host.addEventListener('click', (e) => {
      if (e.target === host) close(null);
    });
    cancel?.addEventListener('click', () => close(null));
    form.addEventListener('keydown', (e) => {
      // Enter in a single-line field submits, whatever produced the key event.
      if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
        e.preventDefault();
        form.requestSubmit();
      }
    });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const values: ModalValues = {};
      new FormData(form).forEach((v, k) => {
        values[k] = String(v);
      });
      close(values);
    });
    document.body.appendChild(host);
    o.onMount?.(form, close);
    const focus = () => form.querySelector<HTMLElement>('input, select, textarea')?.focus();
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(focus);
    else focus();
  });
}

/** A labelled input for a modal body. */
export function field(label: string, input: HTMLElement): HTMLElement {
  return h('label', { class: 'field' }, h('span', null, label), input);
}
